/** What a printout says about itself (WP-25): «Сформировано 08.10.2026, 19:20, период
 *  01.09.2026 — 30.09.2026». The period comes from the page's `DateRange` (it registers its
 *  value here while it is mounted), the moment from the shell when the print starts. A tiny
 *  external store, not React context: the period changes with every preset, and the stamp alone
 *  should re-render, not the shell around the page. */
import { useEffect, useId, useSyncExternalStore } from 'react'

const periods = new Map<string, string>()
const listeners = new Set<() => void>()
let current: string | null = null

function emit(): void {
  const all = [...periods.values()]
  current = all.length > 0 ? (all[all.length - 1] ?? null) : null
  for (const l of listeners) l()
}

function subscribe(l: () => void): () => void {
  listeners.add(l)
  return () => listeners.delete(l)
}

/** The period of the page's report while the caller is mounted: «период 01.09.2026 —
 *  30.09.2026», or «за всё время» for an empty range. The newest registration wins. */
export function usePrintPeriod(text: string | null): void {
  const id = useId()
  useEffect(() => {
    if (text === null) return
    periods.set(id, text)
    emit()
    return () => {
      periods.delete(id)
      emit()
    }
  }, [id, text])
}

/** The period the page registered, or null (a page without dates: «Выплаты»). */
export function usePrintPeriodText(): string | null {
  return useSyncExternalStore(subscribe, () => current, () => null)
}

const STAMP = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

/** «Сформировано 08.10.2026, 19:20» plus the period after a comma when the page has one. */
export function stampText(at: Date, period: string | null): string {
  const when = `Сформировано ${STAMP.format(at)}`
  return period ? `${when}, ${period}` : when
}
